import React, { useState } from "react";
import { ClipboardPaste, X } from "lucide-react";
import { ZONE_COLORS, ZONE_BORDER_COLORS } from "../lib/constants";

const cellCls =
  "w-full bg-transparent border-0 px-2 py-2 text-sm font-mono-tir text-[var(--tir-ink)] focus:outline-none focus:bg-white/60";

/**
 * Parse pasted zones from a spreadsheet.
 * Column order expected (matches the UI): HR range, Speed range, Pace range.
 * If the paste has extra leading columns (e.g., a Name/Zone label column),
 * we trim it so the last 3 non-empty cells become HR/Speed/Pace.
 */
function parsePastedZones(text) {
  if (!text) return [];
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const parsed = lines.map((line) => {
    let cells;
    if (line.includes("\t")) cells = line.split("\t");
    else if (line.includes(",")) cells = line.split(",");
    else cells = line.split(/\s{2,}/);
    cells = cells.map((c) => c.trim());
    // If the row has > 3 cells, drop leading cells so the LAST 3 are HR/Speed/Pace.
    if (cells.length > 3) cells = cells.slice(cells.length - 3);
    while (cells.length < 3) cells.push("");
    return { hr_range: cells[0], speed_range: cells[1], pace_range: cells[2] };
  });
  // drop header row if it looks like column labels
  if (parsed.length > 0) {
    const first = `${parsed[0].hr_range} ${parsed[0].speed_range} ${parsed[0].pace_range}`.toLowerCase();
    if (first.includes("heart") || first.includes("speed") || first.includes("pace") || first.includes("hr") || first.includes("mph") || first.includes("kph") || first.includes("bpm") || first.includes("min/km")) {
      parsed.shift();
    }
  }
  return parsed;
}

export default function ZonesTable({ zones, speedUnit, onChange, readOnly = false }) {
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const updateZone = (idx, field, value) => {
    const next = zones.map((z, i) => (i === idx ? { ...z, [field]: value } : z));
    onChange && onChange(next);
  };

  const handleCellPaste = (e, idx) => {
    const text = e.clipboardData?.getData("text") || "";
    const isMulti = text.includes("\n") || text.includes("\t");
    if (!isMulti) return;
    e.preventDefault();
    const incoming = parsePastedZones(text);
    if (incoming.length === 0) return;
    const merged = zones.map((z) => ({ ...z }));
    for (let i = 0; i < incoming.length; i++) {
      const target = idx + i;
      if (target >= merged.length) break; // zones list is fixed at 5 rows
      const inc = incoming[i];
      for (const key of ["hr_range", "speed_range", "pace_range"]) {
        if (inc[key] !== "" && inc[key] != null) merged[target][key] = inc[key];
      }
    }
    onChange && onChange(merged);
  };

  const applyBulkPaste = () => {
    const incoming = parsePastedZones(pasteText);
    if (incoming.length === 0) return;
    // Map row-by-row starting at zone 0 (Recovery)
    const merged = zones.map((z, i) => {
      const inc = incoming[i];
      if (!inc) return z;
      return {
        ...z,
        hr_range: inc.hr_range || z.hr_range,
        speed_range: inc.speed_range || z.speed_range,
        pace_range: inc.pace_range || z.pace_range,
      };
    });
    onChange && onChange(merged);
    setPasteText("");
    setPasteOpen(false);
  };

  return (
    <div className="w-full" data-testid="zones-table">
      <div className="flex items-start justify-between mb-3 gap-4">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)] font-semibold">
            Prescribed Training Zones
          </div>
          <div className="font-serif-display text-xl text-[var(--tir-ink)] mt-0.5">
            Heart rate · Speed · Pace
          </div>
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={() => setPasteOpen(true)}
            data-testid="paste-zones-btn"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-[var(--tir-ink)] hover:text-[var(--tir-burgundy)] transition-colors"
          >
            <ClipboardPaste size={14} /> Paste zones
          </button>
        )}
      </div>

      {pasteOpen && (
        <div
          className="mb-4 border border-[var(--tir-ink)] bg-white p-5"
          data-testid="paste-zones-dialog"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="font-serif-display text-xl text-[var(--tir-ink)]">
                Paste prescribed zones
              </div>
              <p className="text-xs text-[var(--tir-muted)] mt-1 leading-relaxed max-w-xl">
                Copy the 3 range cells (HR · Speed · Pace) from Excel / Sheets for all 5 zones, top
                to bottom (Recovery → VO2 max). Extra leading columns (like zone name / label) are
                automatically ignored.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setPasteOpen(false)}
              className="text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
              aria-label="Close"
              data-testid="close-paste-zones-dialog"
            >
              <X size={16} />
            </button>
          </div>
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            rows={6}
            placeholder={`<128\t<6.4\t5:45+\n129-144\t6.5-7.6\t4:52-5:44\n145-159\t7.7-8.7\t4:15-4:51\n160-165\t8.8-9.1\t4:04-4:14\n166+\t9.2+\t<4:03`}
            className="w-full bg-[var(--tir-ivory)] border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] p-3 text-sm font-mono-tir focus:outline-none"
            data-testid="paste-zones-textarea"
            autoFocus
          />
          <div className="flex items-center justify-end gap-3 mt-3">
            <button
              type="button"
              onClick={() => setPasteOpen(false)}
              className="text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
              data-testid="cancel-paste-zones-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={applyBulkPaste}
              disabled={!pasteText.trim()}
              data-testid="apply-paste-zones-btn"
              className="bg-[var(--tir-burgundy)] text-white px-5 py-2 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors disabled:opacity-40"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      <div className="w-full overflow-x-auto border border-[var(--tir-border)] bg-white">
        <table className="w-full text-left border-collapse min-w-[640px]">
          <thead>
            <tr className="bg-[#EFE9E6]">
              <th className="px-3 py-3 text-[10px] uppercase tracking-[0.18em] text-[var(--tir-ink)] font-semibold border-b-2 border-[var(--tir-ink)]">
                Zone
              </th>
              <th className="px-3 py-3 text-[10px] uppercase tracking-[0.18em] text-[var(--tir-ink)] font-semibold border-b-2 border-[var(--tir-ink)]">
                HR (bpm)
              </th>
              <th className="px-3 py-3 text-[10px] uppercase tracking-[0.18em] text-[var(--tir-ink)] font-semibold border-b-2 border-[var(--tir-ink)]">
                Speed ({speedUnit})
              </th>
              <th className="px-3 py-3 text-[10px] uppercase tracking-[0.18em] text-[var(--tir-ink)] font-semibold border-b-2 border-[var(--tir-ink)]">
                Pace (min/km)
              </th>
            </tr>
          </thead>
          <tbody>
            {zones.map((z, idx) => {
              const bg = ZONE_COLORS[z.color_key] || "#fff";
              const border = ZONE_BORDER_COLORS[z.color_key] || "#ccc";
              return (
                <tr key={idx} style={{ backgroundColor: bg }} data-testid={`zone-row-${idx}`}>
                  <td
                    className="px-3 py-3 align-top font-serif-display text-[var(--tir-ink)]"
                    style={{ borderLeft: `4px solid ${border}` }}
                  >
                    <div className="text-base leading-tight">{z.zone_label}</div>
                    <div className="text-[11px] tracking-[0.18em] uppercase text-[var(--tir-muted)] mt-0.5">
                      {z.name}
                    </div>
                  </td>
                  <td className="align-top">
                    {readOnly ? (
                      <div className="px-3 py-3 text-sm font-mono-tir">{z.hr_range || "—"}</div>
                    ) : (
                      <input
                        type="text"
                        value={z.hr_range || ""}
                        placeholder="134-153"
                        onChange={(e) => updateZone(idx, "hr_range", e.target.value)}
                        onPaste={(e) => handleCellPaste(e, idx)}
                        className={cellCls}
                        data-testid={`zone-${idx}-hr`}
                      />
                    )}
                  </td>
                  <td className="align-top">
                    {readOnly ? (
                      <div className="px-3 py-3 text-sm font-mono-tir">{z.speed_range || "—"}</div>
                    ) : (
                      <input
                        type="text"
                        value={z.speed_range || ""}
                        placeholder="4.9-5.9"
                        onChange={(e) => updateZone(idx, "speed_range", e.target.value)}
                        onPaste={(e) => handleCellPaste(e, idx)}
                        className={cellCls}
                        data-testid={`zone-${idx}-speed`}
                      />
                    )}
                  </td>
                  <td className="align-top">
                    {readOnly ? (
                      <div className="px-3 py-3 text-sm font-mono-tir">{z.pace_range || "—"}</div>
                    ) : (
                      <input
                        type="text"
                        value={z.pace_range || ""}
                        placeholder="6:14-7:37"
                        onChange={(e) => updateZone(idx, "pace_range", e.target.value)}
                        onPaste={(e) => handleCellPaste(e, idx)}
                        className={cellCls}
                        data-testid={`zone-${idx}-pace`}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <p className="text-xs text-[var(--tir-muted)] mt-2">
          Tip: paste directly into any cell to spill multi-row content across the zones, or use the{" "}
          <span className="font-semibold">Paste zones</span> dialog above. Ranges like{" "}
          <span className="font-mono-tir">4.9-5.9</span>, <span className="font-mono-tir">&lt;4.8</span>,{" "}
          <span className="font-mono-tir">7.5+</span> drive the colored bands on the chart.
        </p>
      )}
    </div>
  );
}
